import { useState } from 'react';
import { customInstance } from '@edison/api-client-react';
import { AlertCircle, TriangleAlert, Info, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button.js';

// Mirror of the server ApportionmentView (services/overhead-apportionment.ts). Kept as a local
// interface — this is the response contract of GET/PUT /api/expenditure/apportionment.
export interface ApportionmentView {
  mode: 'manual' | 'computed' | 'custom';
  customPercent: number | null;
  customReason: string | null;
  effectiveDefaultPercent: number | null;
  computed: {
    percent: number | null;
    rdLabour: number;
    payrollTotal: number;
    incomeYear: string;
    claimMode: string;
    monthsCovered: number;
  };
}

function formatAUD(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(amount);
}

// THE R&D-share derivation line, split so callers can bold the percent. Shared by this card and
// the dashboard payroll provenance panel — one string builder so the two surfaces can never
// disagree on the derivation wording. Returns null when the ratio cannot be computed (Rule Zero:
// no fabricated percent).
export function rdShareParts(
  computed: ApportionmentView['computed'],
): { prefix: string; percentText: string } | null {
  if (computed.percent == null) return null;
  return {
    prefix: `R&D labour ${formatAUD(computed.rdLabour)} ÷ loaded payroll ${formatAUD(computed.payrollTotal)} = `,
    percentText: `${computed.percent}%`,
  };
}

export const MODE_META: Record<ApportionmentView['mode'], { label: string; blurb: string }> = {
  manual: {
    label: 'Manual',
    blurb: 'No pre-fill. Enter an R&D percent for every overhead cost as you approve it.',
  },
  computed: {
    label: 'Computed',
    blurb: 'Pre-fill overhead costs from the R&D-labour-vs-payroll ratio below, so overhead tracks the claim’s own labour mix.',
  },
  custom: {
    label: 'Custom',
    blurb: 'Use your own justified percent (e.g. a board-approved split). The computed ratio is still shown for reference.',
  },
};

// Inline (mid-sentence) claim-mode labels. Keys MUST match the server ClaimMode ids
// (labour-calc.ts) — the derivation line looks up computed.claimMode here, so a stale key silently
// falls through to the raw id (e.g. "annual_salary_cap"). Copy mirrors the claims-UI mode toggle
// (store/claim-mode-store.ts CLAIM_MODE_META), lower-cased for mid-sentence use.
export const CLAIM_MODE_LABELS: Record<string, string> = {
  business_hours: 'business hours',
  business_hours_gross: 'business hours (gross)',
  all_project_hours: 'all project hours',
  classified_rd: 'classified R&D',
  manual_timesheet: 'manual timesheets',
  annual_salary_cap: 'annual salary ceiling',
};

/**
 * Overhead apportionment methodology card. Renders the resolved ApportionmentView: the mode
 * selector, the payroll-derived derivation line (verbatim from data — never a fabricated figure),
 * and the custom percent + reason inputs. When the reconciliation ratio cannot be computed
 * (no Xero payroll synced) it renders an explicit cannot-compute state with no pre-fill (Rule Zero).
 */
export function MethodologyCard({
  incomeYear,
  view,
  isLoading,
  error,
  onRetry,
  onSaved,
}: {
  incomeYear: string;
  view: ApportionmentView | undefined;
  isLoading: boolean;
  error: boolean;
  onRetry: () => void;
  onSaved: (next: ApportionmentView) => void;
}) {
  const [customPercent, setCustomPercent] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function save(mode: ApportionmentView['mode']) {
    setSaving(true);
    setSaveError(null);
    try {
      // For custom, prefer the freshly-typed inputs; fall back to the stored view values so a bare
      // re-select of "custom" without edits re-submits the existing justified percent.
      const pctRaw = customPercent !== '' ? parseFloat(customPercent) : view?.customPercent ?? null;
      const reason = customReason.trim() !== '' ? customReason.trim() : view?.customReason ?? '';

      if (mode === 'custom') {
        if (pctRaw == null || !Number.isFinite(pctRaw) || pctRaw < 0 || pctRaw > 100) {
          setSaveError('Enter a percent between 0 and 100.');
          setSaving(false);
          return;
        }
        if (!reason) {
          setSaveError('A reason is required for a custom percent.');
          setSaving(false);
          return;
        }
      }

      const body: { mode: ApportionmentView['mode']; customPercent?: number; customReason?: string } =
        mode === 'custom' ? { mode, customPercent: pctRaw as number, customReason: reason } : { mode };

      const res = await customInstance<{ data: ApportionmentView }>({
        url: '/api/expenditure/apportionment',
        method: 'PUT',
        params: { incomeYear },
        data: body,
      });
      setCustomPercent('');
      setCustomReason('');
      onSaved(res.data);
    } catch {
      setSaveError('Failed to save the apportionment method. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-5 space-y-3">
        <div className="h-5 w-56 rounded skeleton" />
        <div className="h-16 w-full rounded skeleton" />
      </div>
    );
  }

  if (error || !view) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>Failed to load the overhead apportionment method.</span>
        <button onClick={onRetry} className="ml-auto underline text-xs">Retry</button>
      </div>
    );
  }

  const { computed, mode } = view;
  const derivation = rdShareParts(computed);
  const canCompute = derivation != null;
  const claimModeLabel = CLAIM_MODE_LABELS[computed.claimMode] ?? computed.claimMode;

  return (
    <div className="rounded-lg border bg-card p-5 space-y-4">
      <div className="flex items-start gap-2">
        <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
        <div>
          <h2 className="font-medium text-sm">Overhead apportionment method</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            How overhead costs (admin, subscriptions, insurance) are turned into an R&D-claimable share.
          </p>
        </div>
      </div>

      {/* Mode selector */}
      <div className="flex flex-wrap gap-2">
        {(Object.keys(MODE_META) as Array<ApportionmentView['mode']>).map((m) => {
          const active = mode === m;
          return (
            <button
              key={m}
              type="button"
              onClick={() => { if (!active) void save(m); }}
              disabled={saving}
              aria-pressed={active}
              className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background hover:bg-muted'
              }`}
            >
              {MODE_META[m].label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">{MODE_META[mode].blurb}</p>

      {/* Derivation line — verbatim from the reconciliation figures, or an explicit cannot-compute
          state when payroll hasn't synced (Rule Zero: never fabricate a percent). */}
      {derivation ? (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs font-mono text-foreground/90">
          {derivation.prefix}
          <span className="font-semibold">{derivation.percentText}</span> · {computed.incomeYear} · claim mode:{' '}
          {claimModeLabel} · Xero synced {computed.monthsCovered}/12 months
        </div>
      ) : (
        <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 px-3 py-2 flex items-start gap-2">
          <TriangleAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="text-xs text-amber-800 dark:text-amber-300">
            <p className="font-medium">Cannot compute — sync Xero payroll first</p>
            <p className="text-amber-700 dark:text-amber-400 mt-0.5">
              The R&D-labour-vs-payroll ratio needs synced payroll for {incomeYear}. Until then, computed mode has
              no percent to pre-fill and overhead costs must be apportioned manually.
            </p>
          </div>
        </div>
      )}

      {/* Custom mode: percent + reason inputs, and the custom-vs-computed comparison line. */}
      {mode === 'custom' && (
        <div className="space-y-3 border-t pt-4">
          {view.customPercent != null && (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">custom {view.customPercent}%</span>
              {' · '}
              computed {canCompute ? `${computed.percent}%` : '— (cannot compute)'}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr] gap-3 items-start">
            <div>
              <label className="text-xs font-medium block mb-1" htmlFor="apportionment-custom-pct">
                Custom percent <span className="text-destructive">*</span>
              </label>
              <input
                id="apportionment-custom-pct"
                type="number"
                min="0"
                max="100"
                step="0.1"
                inputMode="decimal"
                value={customPercent}
                onChange={(e) => setCustomPercent(e.target.value)}
                placeholder={view.customPercent != null ? String(view.customPercent) : 'e.g. 40'}
                className="w-full rounded border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1" htmlFor="apportionment-custom-reason">
                Reason / basis <span className="text-destructive">*</span>
              </label>
              <input
                id="apportionment-custom-reason"
                type="text"
                maxLength={2000}
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder={view.customReason ?? 'e.g. Board-approved 40% R&D split for FY2026'}
                className="w-full rounded border bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <Button variant="primary" size="sm" onClick={() => void save('custom')} disabled={saving}>
            <RotateCcw className={`h-3.5 w-3.5 ${saving ? 'animate-spin' : ''}`} aria-hidden="true" />
            {saving ? 'Saving…' : 'Save custom percent'}
          </Button>
        </div>
      )}

      {saveError && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" aria-hidden="true" /> {saveError}
        </p>
      )}
    </div>
  );
}
